import { useCallback, useContext, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouterState } from '@tanstack/react-router'
import { PanelLeftOpen } from 'lucide-react'
import type {ComponentProps} from 'react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarResizeHandle,
  useSidebar,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { SidebarTopBar } from '@/routes/layout/app-layout/components/SidebarTopBar'
import { ConnectionStatusIndicator } from '@/components/app/sync/ConnectionStatusIndicator'
import { AppLayoutContext } from '@/context/AppLayoutContext'
import useLoginStore from '@/store/LoginStore'
import { useMember } from '@/lib/hooks/useWorkspaceUsers'
import { useIsNarrowDesktop } from '@/hooks/useMobile'

export function AppSidebar({
  secSidebar,
  showHeader = true,
  hidden = false,
  ...props
}: ComponentProps<typeof Sidebar> & {
  secSidebar: React.ReactNode | null
  showHeader?: boolean
  /** Sidebar is fully hidden (design 5c); the left edge still peeks it. */
  hidden?: boolean
}) {
  const layoutContext = useContext(AppLayoutContext)
  const userId = useLoginStore((state) => state.userId)
  const { member, isLoading } = useMember(userId)

  const handleResize = useCallback((width: number) => {
    layoutContext?.setSidebarWidth(width)
  }, [layoutContext])

  const displayName = member?.name?.trim()
  const email = member?.email?.trim()

  const sidebarUser = {
    name: displayName || email || 'User',
    username: displayName || email || 'User',
    email: email || undefined,
    avatar: member?.profilePictureUrl || undefined,
    isLoading,
  }

  const { isMobile } = useSidebar()
  const isNarrowDesktop = useIsNarrowDesktop()
  // Settings keeps a fixed sidebar on a wide desktop. On a phone, and on a
  // narrow window where a 300px dock is about half the screen, it is the
  // off-canvas sheet so the page (notifications matrix included) gets the
  // full width (MOL-40, design 15).
  const isSettings = Boolean(secSidebar) && !showHeader
  const settingsAsSheet = isSettings && (isMobile || isNarrowDesktop)
  const collapsible = isSettings
    ? settingsAsSheet ? 'offcanvas' : 'none'
    : hidden ? 'offcanvas' : 'icon'

  return (
    <div className="relative">
      <Sidebar
        collapsible={collapsible}
        forceSheet={settingsAsSheet && !isMobile}
        className="overflow-hidden border-r"
        {...props}
      >
        {showHeader && (
          <SidebarHeader className="p-0">
            <SidebarTopBar user={sidebarUser} />
          </SidebarHeader>
        )}
        <SidebarContent>
          {/* Dynamic content starts immediately */}
          {secSidebar}
        </SidebarContent>
        <SidebarFooter>
          <ConnectionStatusIndicator />
          {collapsible !== 'none' && <PeekHint />}
          {collapsible === 'icon' && <RailControls />}
        </SidebarFooter>
        <SidebarResizeHandle onResize={handleResize} />
      </Sidebar>
      {hidden && <EdgePeekZone />}
      <ClosePeekOnNavigate />
    </div>
  )
}

/**
 * Rail-only footer: the one toggle, which docks the sidebar again (MOL-42).
 * Hiding it completely lives in the workspace menu, so the rail never stacks
 * two look-alike panel buttons.
 */
function RailControls() {
  const { t } = useTranslation('navigation')
  const { state, setOpen } = useSidebar()

  if (state !== 'collapsed') return null

  return (
    <div className="flex flex-col items-center">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label={t('sidebar.dock')}
            onClick={() => setOpen(true)}
          >
            <PanelLeftOpen className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          {t('sidebar.dock')} <kbd className="ml-1 font-mono">[</kbd>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

/** While the menu peeks over the page: how to put it away or keep it (design 5b / 32b). */
function PeekHint() {
  const { t } = useTranslation('navigation')
  const { open, peek, isMobile } = useSidebar()

  if (open || !peek || isMobile) return null

  return (
    <p className="flex items-center gap-1.5 px-2 pt-1 text-[11.5px] text-muted-foreground" data-testid="sidebar-peek-hint">
      {t('sidebar.peekMoveAway')}
      <span aria-hidden>·</span>
      <kbd className="rounded border border-b-2 border-border bg-card px-1 font-mono text-[10.5px] leading-4">[</kbd>
      {t('sidebar.peekDock')}
    </p>
  )
}

/** Hidden mode: pushing the pointer to the left edge peeks the menu. */
function EdgePeekZone() {
  const { peek, setPeek } = useSidebar()
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  if (peek) return null

  return (
    <div
      aria-hidden
      className="fixed inset-y-0 left-0 z-20 hidden w-2 desk:block"
      onPointerEnter={() => {
        timer.current = setTimeout(() => setPeek(true), 120)
      }}
      onPointerLeave={() => {
        if (timer.current) clearTimeout(timer.current)
      }}
    />
  )
}

/** Following a link from the peek should put the menu away. */
function ClosePeekOnNavigate() {
  const { setPeek } = useSidebar()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  useEffect(() => {
    setPeek(false)
  }, [pathname, setPeek])

  return null
}
